import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus, X, Scale } from "lucide-react";
import { COLUMNS, GROUPS, GAINS, TCO, SWITCH_STEPS, COMPARE_FAQ } from "@/lib/compare";
import SEO from "@/lib/seo-static.json";
import { SITE } from "@/lib/site";
import { Container, Section, SectionHeader, Reveal, Kicker, CTAButton, usePageTitle } from "@/components/ui-kit";
import { FAQSection, CTABand } from "@/components/sections/Social";
import { cn } from "@/lib/utils";

const STATE = {
  yes: { icon: Check, cls: "bg-mk-lime/15 text-mk-lime border-mk-lime/40", label: "Included" },
  partial: { icon: Minus, cls: "bg-amber-400/10 text-amber-300 border-amber-400/30", label: "Partial / extra cost" },
  no: { icon: X, cls: "bg-red-400/10 text-red-300 border-red-400/30", label: "Not available" },
};

const Cell = ({ value, highlight, testId }) => {
  const [state, note] = value;
  const S = STATE[state];
  return (
    <div className={cn("flex items-start gap-2.5 px-4 py-3.5 text-sm", highlight && "bg-mk-lime/[0.04]")} data-testid={testId}>
      <span className={cn("grid place-items-center h-5 w-5 rounded-full border shrink-0 mt-0.5", S.cls)} aria-label={S.label}><S.icon className="h-3 w-3" /></span>
      <span className={cn("leading-snug", state === "yes" ? "text-mk-text" : "text-mk-dim")}>{note || S.label}</span>
    </div>
  );
};

export default function Compare() {
  usePageTitle(SEO["/compare"].title, SEO["/compare"].description, SEO["/compare"].image);
  return (
    <div data-testid="compare-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <div className="orb h-[460px] w-[460px] -top-40 right-[-100px] bg-mk-blue/20" aria-hidden />
        <Container className="relative pt-14 pb-12 lg:pt-20 lg:pb-16 text-center">
          <Kicker><Scale className="h-3.5 w-3.5" />Compare · Spreadsheets vs CAFM vs Fixlr</Kicker>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text leading-[1.02] text-balance max-w-4xl mx-auto">Exactly what you gain <span className="text-mk-lime">by switching.</span></h1>
          <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-2xl mx-auto">Most maintenance teams run on a spreadsheet and a WhatsApp group, or a CAFM system that took six months to install. Here's the honest line-by-line.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CTAButton href={SITE.signupUrl} testId="compare-hero-trial-btn">Start free trial <ArrowRight className="h-4 w-4" /></CTAButton>
            <CTAButton to="/pricing" variant="ghost" testId="compare-hero-pricing-btn">See pricing</CTAButton>
          </div>
        </Container>
      </section>

      <Section className="pt-4 lg:pt-6">
        <Container>
          <Reveal className="rounded-3xl border border-mk-border bg-mk-surface overflow-hidden" data-testid="compare-table">
            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] border-b border-mk-border sticky top-[72px] z-10 bg-mk-surface/95 backdrop-blur">
                  <div className="px-5 py-5 text-xs font-mono uppercase tracking-[0.2em] text-mk-muted self-end">Capability</div>
                  {COLUMNS.map((c) => (
                    <div key={c.key} className={cn("px-4 py-5", c.highlight && "bg-mk-lime/[0.06] border-x border-mk-lime/30")} data-testid={`compare-col-${c.key}`}>
                      <div className={cn("font-display text-lg font-bold", c.highlight ? "text-mk-lime" : "text-mk-text")}>{c.name}</div>
                      <div className="text-xs text-mk-muted mt-0.5">{c.sub}</div>
                    </div>
                  ))}
                </div>
                {GROUPS.map((g) => (
                  <div key={g.name} data-testid={`compare-group-${g.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] bg-mk-bg/60 border-b border-mk-border">
                      <div className="col-span-4 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] text-mk-lime">{g.name}</div>
                    </div>
                    {g.rows.map((r) => (
                      <div key={r.label} className="grid grid-cols-[1.2fr_1fr_1fr_1fr] border-b border-mk-border/70 last:border-b-0 hover:bg-mk-raised/30 transition-colors">
                        <div className="px-5 py-3.5 text-sm font-medium text-mk-text">{r.label}</div>
                        {COLUMNS.map((c) => <Cell key={c.key} value={r[c.key]} highlight={c.highlight} />)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-5 px-5 py-4 border-t border-mk-border text-xs text-mk-muted">
              {Object.entries(STATE).map(([k, S]) => <span key={k} className="inline-flex items-center gap-1.5"><span className={cn("grid place-items-center h-4 w-4 rounded-full border", S.cls)}><S.icon className="h-2.5 w-2.5" /></span>{S.label}</span>)}
              <span className="ml-auto">"Legacy CAFM" describes typical category capabilities, not a specific vendor.</span>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Section className="bg-mk-surface/40 border-y border-mk-border">
        <Container>
          <SectionHeader eyebrow="What customers gained" title="Numbers from teams who switched." />
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {GAINS.map((g, i) => (
              <Reveal key={g.label} delay={i * 0.06} className="rounded-2xl border border-mk-border bg-mk-bg p-6" data-testid={`compare-gain-${i}`}>
                <div className="font-display text-3xl font-bold text-mk-lime tracking-tight">{g.value}</div>
                <div className="mt-2 text-sm text-mk-text">{g.label}</div>
                <div className="mt-1 text-xs text-mk-muted">{g.source}</div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5 min-w-0">
              <SectionHeader eyebrow="Total cost" title="The spreadsheet isn't free." desc="Licence fees are the visible cost. Admin hours re-typing, chasing and reconciling are the invisible one — and they scale with every job." />
              <CTAButton to="/#roi" variant="ghost" className="mt-6" testId="compare-roi-btn">Run your own numbers <ArrowRight className="h-4 w-4" /></CTAButton>
            </div>
            <Reveal className="lg:col-span-7 min-w-0 rounded-2xl border border-mk-border bg-mk-surface overflow-hidden" data-testid="compare-tco">
              <div className="px-5 py-4 border-b border-mk-border font-display font-semibold text-mk-text">{TCO.title}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead><tr className="text-[11px] font-mono uppercase tracking-wider text-mk-muted"><th className="text-left px-5 py-3">Stack</th><th className="text-left px-3 py-3">Licences</th><th className="text-left px-3 py-3">Admin hours</th><th className="text-left px-3 py-3">Setup</th><th className="text-right px-5 py-3">Monthly</th></tr></thead>
                  <tbody>
                    {TCO.rows.map((r) => {
                      const col = COLUMNS.find((c) => c.key === r.key);
                      return (
                        <tr key={r.key} className={cn("border-t border-mk-border/70", r.highlight && "bg-mk-lime/[0.05]")} data-testid={`compare-tco-${r.key}`}>
                          <td className={cn("px-5 py-3.5 font-medium", r.highlight ? "text-mk-lime" : "text-mk-text")}>{col.name}</td>
                          <td className="px-3 py-3.5 font-mono text-mk-dim">{r.licence}</td>
                          <td className="px-3 py-3.5 font-mono text-mk-dim">{r.hidden}</td>
                          <td className="px-3 py-3.5 font-mono text-mk-dim">{r.setup}</td>
                          <td className={cn("px-5 py-3.5 font-mono text-right font-semibold", r.highlight ? "text-mk-lime" : "text-mk-text")}>{r.total}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 text-[11px] text-mk-muted border-t border-mk-border">{TCO.note}</div>
            </Reveal>
          </div>
        </Container>
      </Section>

      <Section className="bg-mk-surface/40 border-y border-mk-border">
        <Container>
          <SectionHeader eyebrow="Switching" title="Live in two weeks, not two quarters." />
          <div className="mt-10 grid md:grid-cols-4 gap-4">
            {SWITCH_STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07} className="rounded-2xl border border-mk-border bg-mk-bg p-6" data-testid={`compare-step-${i}`}>
                <div className="font-mono text-xs text-mk-lime">{s.day}</div>
                <h3 className="mt-3 font-display text-lg font-semibold text-mk-text">{s.title}</h3>
                <p className="mt-2 text-sm text-mk-dim leading-relaxed">{s.desc}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-8 text-center text-sm text-mk-dim">Prefer a guided migration? <Link to="/contact" className="text-mk-lime hover:underline" data-testid="compare-migration-link">Book an onboarding call</Link>.</div>
        </Container>
      </Section>

      <FAQSection items={COMPARE_FAQ} title="Switching — common questions." eyebrow="FAQ" />
      <CTABand title="Ready to retire the spreadsheet?" />
    </div>
  );
}
